import { ExternalLinkIcon } from "@chakra-ui/icons";
import {
  Box,
  Center,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  HStack,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Show,
  Spacer,
  useBreakpointValue,
  useDisclosure,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import { Moment } from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import { NavigationLink } from "adminComponents";
import { AssignmentsIcon } from "adminComponents/atoms/AssignmentsIcon";
import { Avatar, IProps as AvatarProps } from "adminComponents/atoms/Avatar";
import {
  Breadcrumb,
  BreadcrumbNavigationLink,
} from "adminComponents/atoms/Breadcrumb";
import { Button } from "adminComponents/atoms/Button";
import { Divider } from "adminComponents/atoms/Divider";
import { Heading } from "adminComponents/atoms/Heading";
import { Icon } from "adminComponents/atoms/Icon";
import { IconCTAButton } from "adminComponents/atoms/IconCTAButton";
import { Logo } from "adminComponents/atoms/Logo";
import { Tag } from "adminComponents/atoms/Tag";
import { Text } from "adminComponents/atoms/Text";
import { TeacherAccountMenu } from "adminComponents/molecules/TeacherAccountMenu";
import { pxToRem } from "adminComponents/utils/pxToRem";
import { PREMIUM_SUPPORT_URL } from "links/lib/constants";
import {
  IGradeLevel,
  ILicense,
  ISubject,
  ITeacherOnboardingChecklist,
  IUser,
  UserRole,
} from "links/lib/types";

import { Link } from "./components/Link";

export interface IProps {
  avatarData: AvatarProps;
  breadcrumbs?: BreadcrumbNavigationLink[];
  logoLink: NavigationLink;
  navigationLinks?: NavigationLink[];
  authActionLinks: NavigationLink[];
  resourceLinks: NavigationLink[];
  accountLinks: NavigationLink[];
  user?: IUser;
  userLicense?: ILicense;
  showGracePeriodBanner?: boolean;
  handleDeferGracePeriodBanner?: () => void;
  gracePeriodEndDate?: Moment;
  variant?: string;
  isLoading?: boolean;
  assignmentCount?: number;
  handleCreatePracticeSet?: () => void;
  handleOpenAssignmentsFlyout?: () => void;
  handleLogIn?: () => void;
  handleSignUp?: () => void;
  isGrayscale?: boolean;
  activeNavigationPath?: string;
  showPremiumMarker?: boolean;
  subjects: ISubject[];
  gradeLevels: IGradeLevel[];
  hasNoPremiumAccess?: boolean;
  adminMode?: boolean;
  teacherOnboardingChecklist?: ITeacherOnboardingChecklist;
}

export const NavigationHeader: React.FC<IProps> = ({
  avatarData,
  breadcrumbs,
  logoLink,
  navigationLinks,
  authActionLinks,
  resourceLinks,
  accountLinks,
  user,
  userLicense,
  showGracePeriodBanner = false,
  handleDeferGracePeriodBanner,
  gracePeriodEndDate,
  variant = "default",
  isLoading = false,
  assignmentCount,
  handleCreatePracticeSet,
  handleOpenAssignmentsFlyout,
  handleLogIn,
  handleSignUp,
  isGrayscale = false,
  activeNavigationPath,
  showPremiumMarker = true,
  hasNoPremiumAccess = false,
  adminMode,
}) => {
  const [showMenu, toggleShowMenu] = useState(false);
  const [isNavTransparent, toggleIsNavTransparent] = useState(true);
  const [isBreadcrumbVisible, toggleIsBreadcrumbVisible] = useState(true);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const menuRef = useRef();
  const navTopRowRef = useRef<HTMLDivElement>(null);
  const navBottomRowRef = useRef<HTMLDivElement>(null);
  const isMobile = useBreakpointValue({ base: true, md: false });
  const isMediumBreakpoint = useBreakpointValue({
    base: false,
    md: true,
    lg: false,
  });
  const isMediumOrSmallerBreakpoint = useBreakpointValue({
    base: true,
    lg: false,
  });

  const styles = useMultiStyleConfig("AdminNavigationHeader", {
    showMenu,
    isNavTransparent,
    isBreadcrumbVisible,
    isGrayscale,
    variant,
    showGracePeriodBanner,
    isMobile,
  });

  const handleAvatarClick = () => {
    toggleShowMenu((prev) => !prev);
  };

  const handlePremiumClick = () => {
    window.location.href = PREMIUM_SUPPORT_URL;
  };

  useEffect(() => {
    if (navTopRowRef.current) {
      const callback = (event: Event) => {
        const { detail } = event as CustomEvent;
        if (detail.shouldBeTransparent && detail.effectedElement === "topNav") {
          toggleIsNavTransparent(true);
        } else if (detail.effectedElement === "topNav") {
          toggleIsNavTransparent(false);
        }
      };

      window.addEventListener("nav-intersect", callback);

      return () => window.removeEventListener("nav-intersect", callback);
    }
  }, []);

  useEffect(() => {
    if (navBottomRowRef.current) {
      const callback = (event: Event) => {
        const { detail } = event as CustomEvent;
        if (
          detail.shouldBeTransparent &&
          detail.effectedElement === "breadcrumb"
        ) {
          toggleIsBreadcrumbVisible(true);
        } else if (detail.effectedElement === "breadcrumb") {
          toggleIsBreadcrumbVisible(false);
        }
      };

      window.addEventListener("nav-intersect", callback);

      return () => window.removeEventListener("nav-intersect", callback);
    }
  }, [isLoading]);

  const { t } = useTranslation("admin", { useSuspense: false });

  return (
    <Box
      __css={styles.outer}
      height={{
        base: "100%",
        md: "50%",
        xl: "25%",
      }}
    >
      <Box
        __css={styles.firstRow}
        ref={navTopRowRef}
        backgroundColor={adminMode ? "primary.golden-light" : undefined}
      >
        <Box __css={styles.navContainerMobile}>
          <Button
            ref={menuRef.current}
            onClick={onOpen}
            variant="adminInlineIconButton"
            aria-label={t("common.openMenu")}
          >
            <Icon icon="menu" />
          </Button>
          <Drawer
            size="full"
            isOpen={isOpen}
            placement="left"
            onClose={onClose}
            finalFocusRef={menuRef.current}
            variant="adminNavigation"
          >
            <DrawerOverlay />
            <DrawerContent>
              <DrawerCloseButton
                as={IconCTAButton}
                ariaLabel={t("common.close")}
              />
              <DrawerBody>
                <Flex direction="column" h="full">
                  {navigationLinks?.map((nl, index) => (
                    <Box key={index} __css={styles.navItemMobile}>
                      <Link as={RouterLink} to={nl.to}>
                        <Flex alignItems="center">
                          <Heading as="p" variant="adminH5">
                            {nl.label}
                          </Heading>
                          {nl.isExternal && (
                            <ExternalLinkIcon mx={pxToRem(8)} />
                          )}
                        </Flex>
                      </Link>
                      <Divider color="primary.tan" />
                    </Box>
                  ))}
                  {(user?.role === UserRole.Admin ||
                    user?.role === UserRole.Teacher ||
                    user?.role === UserRole.ContentSpecialist) &&
                    !!handleCreatePracticeSet && (
                      <>
                        <Spacer />
                        <Button
                          onClick={handleCreatePracticeSet}
                          size="sm"
                          variant="adminButtonFilled"
                          mb={pxToRem(16)}
                        >
                          {t(
                            "adminNavigationHeader.navigationLinks.buttonCreatePracticeSet"
                          )}
                        </Button>
                      </>
                    )}
                  {user?.role === UserRole.Admin && adminMode && (
                    <>
                      <Spacer />
                      <Button
                        size="sm"
                        as={RouterLink}
                        to="/t/home"
                        variant="adminButtonFilled"
                      >
                        <Flex gap={pxToRem(8)}>
                          <Icon
                            as="span"
                            aria-hidden="true"
                            icon={"open_in_new"}
                          />
                          {t(
                            "adminNavigationHeader.navigationLinks.buttonCloseAdminMode"
                          )}
                        </Flex>
                      </Button>
                    </>
                  )}
                </Flex>
              </DrawerBody>
            </DrawerContent>
          </Drawer>
        </Box>
        <HStack>
          <Box display="inline" id="psi_launcher"></Box>
          <Logo width={pxToRem(154)} navigationLink={logoLink} />
          {adminMode && !isMediumOrSmallerBreakpoint && (
            <Tag
              colorScheme="dark-gray"
              leftIcon="setting_outlined"
              color="primary.white"
            >
              {t("adminNavigationHeader.general.adminTools")}
            </Tag>
          )}
        </HStack>
        <Box __css={styles.navContainerDesktop}>
          {navigationLinks?.map((nl, index) => (
            <Link
              as={RouterLink}
              key={index}
              to={nl.to}
              variant="adminNavLink"
              aria-current={activeNavigationPath === nl.to}
              target={nl.isExternal ? "_blank" : undefined}
            >
              <Flex alignItems="center">
                {nl.label}
                {nl.isExternal && <ExternalLinkIcon mx={pxToRem(8)} />}
              </Flex>
            </Link>
          ))}
        </Box>
        <Box sx={styles.avatarNavContainer}>
          <Box __css={styles.avatarContainer}>
            <Flex alignItems="center" gap={pxToRem(10)}>
              {user?.role === UserRole.Student && (
                <Box __css={styles.assignments}>
                  <AssignmentsIcon
                    assignmentCount={assignmentCount}
                    onClick={handleOpenAssignmentsFlyout}
                    ariaLabel={t("common.openMenu")}
                  />
                </Box>
              )}
              {(user?.role === UserRole.Admin ||
                user?.role === UserRole.Teacher ||
                user?.role === UserRole.ContentSpecialist) &&
                !!handleCreatePracticeSet && (
                  <Show above="md">
                    <Button
                      onClick={handleCreatePracticeSet}
                      size="sm"
                      variant="adminButtonFilled"
                      leftIcon={
                        isMediumBreakpoint ? <Icon icon="add" /> : <></>
                      }
                    >
                      {isMediumBreakpoint
                        ? t(
                            "adminNavigationHeader.navigationLinks.buttonPracticeSet"
                          )
                        : t(
                            "adminNavigationHeader.navigationLinks.buttonCreatePracticeSet"
                          )}
                    </Button>
                  </Show>
                )}
              {user?.role === UserRole.Admin && adminMode && (
                <Show above="md">
                  <Button
                    size="sm"
                    as={RouterLink}
                    to="/t/home"
                    variant="adminButtonFilled"
                  >
                    <Flex gap={pxToRem(8)}>
                      <Icon as="span" aria-hidden="true" icon={"open_in_new"} />
                      {t(
                        "adminNavigationHeader.navigationLinks.buttonCloseAdminMode"
                      )}
                    </Flex>
                  </Button>
                </Show>
              )}
              {!user && (
                <Show above="md">
                  {handleLogIn && (
                    <Button
                      onClick={handleLogIn}
                      size="sm"
                      variant="adminButtonOutlined"
                      bgColor="white"
                    >
                      {t("adminNavigationHeader.navigationLinks.buttonLogIn")}
                    </Button>
                  )}
                  {handleSignUp && (
                    <Button
                      onClick={handleSignUp}
                      size="sm"
                      variant="adminButtonFilled"
                    >
                      {t("adminNavigationHeader.navigationLinks.buttonSignUp")}
                    </Button>
                  )}
                </Show>
              )}
              {(user?.role === UserRole.Student && !isMobile) ||
              user?.role === UserRole.Admin ||
              user?.role === UserRole.Teacher ||
              user?.role === UserRole.ContentSpecialist ? (
                <Popover
                  variant="adminProfileMenu"
                  isOpen={!isMediumOrSmallerBreakpoint && showMenu}
                  onOpen={handleAvatarClick}
                  onClose={handleAvatarClick}
                  placement="bottom-end"
                >
                  <PopoverTrigger>
                    <Box>
                      <Avatar
                        {...avatarData}
                        handleClick={() => {
                          /* do nothing, popover triggers opening of 
                          TeacherAccountMenu */
                        }}
                        size="sm"
                        variant="adminAvatar"
                        showPremiumMarker={
                          showPremiumMarker && !hasNoPremiumAccess
                        }
                      />
                    </Box>
                  </PopoverTrigger>
                  <PopoverContent>
                    <TeacherAccountMenu
                      user={user}
                      accountLinks={accountLinks}
                      handleClose={handleAvatarClick}
                      resourceLinks={resourceLinks}
                      authActionLinks={authActionLinks}
                      showPremiumMarker={showPremiumMarker}
                    />
                  </PopoverContent>
                </Popover>
              ) : undefined}
            </Flex>
            <Box __css={styles.avatarMenu}>
              {(user?.role === UserRole.Admin ||
                user?.role === UserRole.Teacher ||
                user?.role === UserRole.ContentSpecialist) && (
                <Drawer
                  size="full"
                  isOpen={!!isMediumOrSmallerBreakpoint && showMenu}
                  placement="left"
                  onClose={handleAvatarClick}
                  variant="adminNavigation"
                >
                  <DrawerOverlay />
                  <DrawerContent>
                    <DrawerCloseButton
                      as={IconCTAButton}
                      ariaLabel={t("common.close")}
                    />
                    <DrawerBody>
                      <TeacherAccountMenu
                        user={user}
                        accountLinks={accountLinks}
                        handleClose={handleAvatarClick}
                        resourceLinks={resourceLinks}
                        authActionLinks={authActionLinks}
                        showPremiumMarker={showPremiumMarker}
                      />
                    </DrawerBody>
                  </DrawerContent>
                </Drawer>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      {!isLoading && showGracePeriodBanner && (
        <Center __css={styles.gracePeriodBanner}>
          <Icon icon="timer" mr={pxToRem(20)} />
          <Box>
            <Text variant="adminP1Bold" display="inline">
              {t("adminNavigationHeader.premiumGracePeriodBanner.expired", {
                trialOrLicense: userLicense?.is_trial
                  ? t("common.trial")
                  : t("common.license"),
              })}
            </Text>
            &nbsp;
            <Text variant="adminP1" display="inline">
              {t("adminNavigationHeader.premiumGracePeriodBanner.description", {
                endDate: gracePeriodEndDate?.format("MM/DD/YY"),
              })}
            </Text>
            &nbsp;
            <Text
              variant="adminLinkLarge"
              color="utility.link"
              cursor="pointer"
              onClick={() => handlePremiumClick()}
              display="inline"
            >
              {t("adminNavigationHeader.premiumGracePeriodBanner.upgrade")}
            </Text>
          </Box>
          <Icon
            cursor="pointer"
            icon="close"
            boxSize={pxToRem(18)}
            ml={pxToRem(40)}
            onClick={() => handleDeferGracePeriodBanner?.()}
          />
        </Center>
      )}
      {!isLoading && breadcrumbs && (
        <Box __css={styles.secondRow} ref={navBottomRowRef}>
          <Breadcrumb breadcrumbs={breadcrumbs} />
        </Box>
      )}
    </Box>
  );
};
